import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function useUOMList() {
  // Use toast
  const toast = useToast();

  const refUOMListTable = ref(null);

  // Loading state
  const isTableLoading = ref(false);

  // Table Handlers
  const tableColumns = [
    { key: 'serial', label: 'SNo', sortable: false, thStyle: { minWidth: '60px' } },
    { key: 'id', label: 'ID', sortable: false, thStyle: { minWidth: '60px' } },
    { key: 'name', sortable: false, thStyle: { minWidth: '150px' }, stickyColumn: true },
    { key: 'description', sortable: false, thStyle: { minWidth: '250px' } },
    { key: 'isActive', label: 'Status', sortable: false, thStyle: { minWidth: '100px' } },
    { key: 'createdBy', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'updatedBy', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'actions', sortable: false, stickyColumn: true, thStyle: { minWidth: '120px' } },
  ];
  const perPage = ref(10);
  const totalUOMs = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');

  // Filter states
  const isActiveFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refUOMListTable.value ? refUOMListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUOMs.value,
    };
  });

  const refetchData = () => {
    // Force the table to refresh by triggering a new API call
    if (refUOMListTable.value) {
      // Reset to first page when filters change
      currentPage.value = 1;
      // Trigger the table refresh which will call fetchUOMs with new filter values
      refUOMListTable.value.refresh();
    }
  };

  // Debounced search function
  const debouncedSearch = debounce(() => {
    refetchData();
  }, 500); // 500ms delay

  // Watch for page and perPage changes (immediate)
  watch([currentPage, perPage], () => {
    refetchData();
  });

  // Watch for search query changes (debounced)
  watch(searchQuery, () => {
    debouncedSearch();
  });

  // Watch for filter changes
  watch(isActiveFilter, (newValue, oldValue) => {
    console.log('=== useUOMList isActiveFilter changed:', { oldValue, newValue });
    refetchData();
  });

  const fetchUOMs = (ctx, callback) => {
    console.log('=== useUOMList fetchUOMs()');

    isTableLoading.value = true;

    const params = {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      isActive: isActiveFilter.value,
    };

    console.log('=== useUOMList fetchUOMs() params:', params);

    store
      .dispatch('fbr-invoice-unit-of-measure/fetchUOMs', params)
      .then((response) => {
        console.log('=== useUOMList fetchUOMs() response', response);

        const { units, pagination } = response;
        console.log('=== useUOMList units:', units);
        console.log('=== useUOMList pagination:', pagination);

        callback(units);
        totalUOMs.value = pagination.totalCount;
      })
      .catch((error) => {
        console.log('=== useUOMList fetchUOMs() error', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Unit of Measures list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      })
      .finally(() => {
        isTableLoading.value = false;
      });
  };

  return {
    fetchUOMs,
    tableColumns,
    perPage,
    currentPage,
    totalUOMs,
    dataMeta,
    perPageOptions,
    searchQuery,
    refUOMListTable,

    // Loading state
    isTableLoading,

    refetchData,

    // Filter states
    isActiveFilter,
  };
}
